<template>
  <li class="sbl">
    <router-link :to="to" class="sbl__link" :class="{ 'sbl__link--slim': slim }" exact-active-class="sbl__link--active" :title="slim ? name : undefined">
      <span class="sbl__icon">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" v-html="icon" />
      </span>
      <span class="sbl__label" :class="{ 'sbl__label--slim': slim }"><slot /></span>
      <span v-if="badge > 0" class="sbl__badge" :class="{ 'sbl__badge--slim': slim }">
        {{ badge > 99 ? "99+" : badge }}
      </span>
      <span v-if="badge > 0 && slim" class="sbl__dot" />
    </router-link>
  </li>
</template>

<script>
export default {
  props: {
    to: { type: String, required: true },
    icon: { type: String, required: true },
    name: { type: String, default: "" },
    slim: { type: Boolean, default: false },
    badge: { type: Number, default: 0 },
  },
};
</script>

<style scoped>
/* ─── Token local (sincronizado con el brand navy del sidebar) ─── */
.sbl {
  --navy: #355e91;
  --navy-light: rgba(53, 94, 145, 0.08);
  --navy-mid: rgba(53, 94, 145, 0.14);
  list-style: none;
}

/* ── Link base ── */
.sbl__link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  color: #475467;
  text-decoration: none;
  font-size: 13.5px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  transition: background 0.14s ease, color 0.14s ease;

  /* Barra indicadora (oculta por defecto) */
  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%) scaleY(0);
    width: 3px;
    height: 60%;
    background: var(--navy);
    border-radius: 0 3px 3px 0;
    transition: transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
}

/* ── Ícono ── */
.sbl__icon {
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f1f4f8;
  color: #64748b;
  transition: background 0.16s ease, color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

/* ── Label ── */
.sbl__label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 160px;
  opacity: 1;
  transition: max-width 0.16s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.12s ease;
}

.sbl__label--slim {
  max-width: 0;
  opacity: 0;
  pointer-events: none;
}

/* ── Badge ── */
.sbl__badge {
  flex-shrink: 0;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e9eaec;
  color: #374151;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  max-width: 60px;
  opacity: 1;
  transition: max-width 0.16s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.12s ease;
}

.sbl__badge--slim {
  max-width: 0;
  opacity: 0;
  padding: 0;
  min-width: 0;
}

/* Dot de notificación en slim */
.sbl__dot {
  position: absolute;
  top: 7px;
  right: 7px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--navy);
  border: 1.5px solid #fff;
}

/* ── Hover ── */
.sbl__link:hover {
  background: var(--navy-light);
  color: #1f2937;
}

.sbl__link:hover:not(.sbl__link--active) .sbl__icon {
  background: var(--navy-mid);
  color: var(--navy);
  transform: translateY(-1px);
}

.sbl__link:focus-visible {
  outline: 2px solid var(--navy);
  outline-offset: 2px;
}

/* ── Estado activo ── */
.sbl__link--active {
  background: var(--navy-light);
  color: var(--navy);
  font-weight: 600;

  /* Barra indicadora visible */
  &::before {
    transform: translateY(-50%) scaleY(1);
  }
}

.sbl__link--active .sbl__icon {
  background: var(--navy);
  color: #fff;
  box-shadow: 0 3px 8px rgba(53, 94, 145, 0.32);
}

.sbl__link--active svg {
  stroke: #fff;
}

.sbl__link--active .sbl__badge {
  background: var(--navy-mid);
  color: var(--navy);
}

.sbl__link--active .sbl__dot {
  background: var(--navy);
  border-color: rgba(53, 94, 145, 0.15);
}

/* ── Slim ── */
.sbl__link--slim {
  justify-content: center;
  padding: 7px;
}

/* En slim el hover y activo recaen sobre el icono */
.sbl__link--slim:hover {
  background: transparent;
}

.sbl__link--slim:hover:not(.sbl__link--active) .sbl__icon {
  background: var(--navy-mid);
  color: var(--navy);
  transform: none;
}

/* Slim activo: barra izquierda + icono navy */
.sbl__link--slim.sbl__link--active {
  background: transparent;

  &::before {
    transform: translateY(-50%) scaleY(1);
  }
}

.sbl__link--slim.sbl__link--active .sbl__icon {
  background: var(--navy);
  color: #fff;
  box-shadow: 0 3px 8px rgba(53, 94, 145, 0.32);
}

/* ── Movimiento reducido ── */
@media (prefers-reduced-motion: reduce) {
  .sbl__link,
  .sbl__icon,
  .sbl__label,
  .sbl__badge,
  .sbl__link::before {
    transition-duration: 0.001ms !important;
  }
  .sbl__link:hover .sbl__icon {
    transform: none;
  }
}
</style>
